import React, { useEffect, useMemo, useState } from "react";
import { useTeam } from '../../../shared/context/teamContext';
import { Modal, message } from 'antd';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import CreateCampaignForm from "./CreateCampaignForm";
import CampaignDetailsModal from '../../../shared/components/modals/CampaignDetailsModal';
import CampaignDetailView from '../components/CampaignDetailView';
import { Plus, X, Search, ChevronDown, ChevronUp } from "lucide-react";
import Icon from '../../../shared/components/Icon';
import LiquidFillLoader from '../../../shared/components/ui/LiquidFillLoader';

const Campaigns = () => {
  const { selectedTeam } = useTeam();

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentcolor: "#0f172a",
    borderColor: "#DDE2EE"
  };

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [expandedCampaigns, setExpandedCampaigns] = useState(new Set());
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [tableRowsPerPage, setTableRowsPerPage] = useState(50);

  useEffect(() => {
    if (selectedTeam?._id) {
      fetchCampaigns();
    }
  }, [selectedTeam]);

  const fetchCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (selectedTeam?._id) {
        params.teamId = selectedTeam._id;
      }

      const response = await axiosInstance.get("/campaign-links/", { params });
      setCampaigns(response.data.links || []);
    } catch (err) {
      console.error("Error fetching campaigns:", err);
      setError(err.response?.data?.error || "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCampaign = (campaignId) => {
    const campaign = campaigns.find(c => c._id === campaignId);
    setCampaignToDelete(campaign);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!campaignToDelete) return;
    
    setDeleting(true);
    try {
      await axiosInstance.delete(`/campaign-links/${campaignToDelete._id}`);
      message.success('Campaign deleted successfully');
      setDeleteModalVisible(false);
      setCampaignToDelete(null);
      fetchCampaigns();
    } catch (err) {
      console.error("Error deleting campaign:", err);
      message.error(err.response?.data?.error || "Failed to delete campaign");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setCampaignToDelete(null);
  };

  const openDetailsModal = (campaign) => {
    setSelectedCampaign(campaign);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setSelectedCampaign(null);
    setShowDetailsModal(false);
  };

  const toggleCampaign = (campaignId) => {
    const newExpanded = new Set(expandedCampaigns);
    if (newExpanded.has(campaignId)) {
      newExpanded.delete(campaignId);
    } else {
      newExpanded.add(campaignId);
    }
    setExpandedCampaigns(newExpanded);
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    fetchCampaigns();
  };

  const handleLinkCreated = () => {
    setSuccess("UTM link created successfully!");
    setTimeout(() => setSuccess(null), 4000);
    fetchCampaigns();
  };

  // Filter campaigns based on search term
  const filteredCampaigns = useMemo(() => {
    if (!searchTerm.trim()) {
      return campaigns;
    }
    const searchLower = searchTerm.toLowerCase().trim();
    return campaigns.filter(campaign => {
      const name = (campaign.name || '').toLowerCase();
      const utmId = (campaign.utm?.utm_id || '').toLowerCase();
      const medium = (campaign.utm?.medium || '').toLowerCase();
      return name.includes(searchLower) || 
             utmId.includes(searchLower) || 
             medium.includes(searchLower);
    });
  }, [campaigns, searchTerm]);

  const paginatedCampaigns = useMemo(() => {
    if (filteredCampaigns.length <= tableRowsPerPage) {
      return filteredCampaigns;
    }
    return filteredCampaigns.slice(0, tableRowsPerPage);
  }, [filteredCampaigns, tableRowsPerPage]);

  const renderTableBody = () => {
    if (loading) {
      return (
        <tr>
          <td colSpan={6} className="px-6 py-10">
            <div className="flex flex-col items-center justify-center">
              <LiquidFillLoader size={100} />
              <span className="mt-4 text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                Loading campaigns...
              </span>
            </div>
          </td>
        </tr>
      );
    }

    if (campaigns.length === 0) {
      return (
        <tr>
          <td colSpan={6} className="px-6 py-12 text-center text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            No campaigns created yet. Generate your first UTM link to get started.
          </td>
        </tr>
      );
    }

    if (filteredCampaigns.length === 0) {
      return (
        <tr>
          <td colSpan={6} className="px-6 py-12 text-center text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            No campaigns found matching "{searchTerm}".
          </td>
        </tr>
      );
    }

    return paginatedCampaigns.map((campaign) => {
      const isExpanded = expandedCampaigns.has(campaign._id);
      return (
        <React.Fragment key={campaign._id}>
          <tr
            className="cursor-pointer"
            style={{
              borderBottom: isExpanded ? 'none' : `1px solid ${styles.borderColor}`,
              backgroundColor: 'transparent'
            }}
            onClick={() => toggleCampaign(campaign._id)}
          >
        <td className="px-6 py-4 text-sm font-medium" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
          {campaign.name}
        </td>
        <td className="px-6 py-4 text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          {campaign.utm?.utm_id || "-"}
        </td>
        <td className="px-6 py-4 text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          {campaign.utm?.medium || "-"}
        </td>
        <td className="px-6 py-4 text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          {campaign.clicks ?? 0}
        </td>
        <td className="px-6 py-4 text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          {campaign.updatedAt
            ? new Date(campaign.updatedAt).toLocaleDateString()
            : "-"}
        </td>
        <td className="px-6 py-4 text-sm text-right">
          <div className="flex items-center justify-end gap-3">
            {isExpanded ? (
              <ChevronUp size={16} style={{ color: styles.textSecondary }} />
            ) : (
              <ChevronDown size={16} style={{ color: styles.textSecondary }} />
            )}
            <button
              onClick={(event) => {
                event.stopPropagation();
                handleDeleteCampaign(campaign._id);
              }}
              style={{ color: styles.textSecondary }}
              className="hover:opacity-70 transition-opacity duration-150"
              title="Delete campaign"
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#DC2626';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = styles.textSecondary;
              }}
            >
              <Icon name="delete" size={16} />
            </button>
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={6} style={{ borderBottom: `1px solid ${styles.borderColor}`, backgroundColor: 'transparent' }}>
            <div className="px-6 py-4">
              <CampaignDetailView campaign={campaign} teamId={selectedTeam?._id || null} />
            </div>
          </td>
        </tr>
      )}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="flex-1 overflow-auto px-0 py-0 space-y-4">
        {success && (
          <div className="p-4 bg-green-50 border border-green-200 text-green-800 rounded-xl">
            {success}
          </div>
        )}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-800 rounded-xl">
            {error}
          </div>
        )}

        <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
          <div className="overflow-x-auto">
            <table className="w-full" style={{ fontFamily: 'Raleway, sans-serif' }}>
              <thead>
                <tr style={{ backgroundColor: 'transparent', borderBottom: `2px solid ${styles.borderColor}` }}>
                  <th colSpan={6} className="px-6 py-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 max-w-md">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#475569]" />
                          <input
                            type="text"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-10 py-1 text-sm bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d0c46] focus:border-transparent"
                            style={{ fontFamily: 'Raleway, sans-serif' }}
                          />
                          {searchTerm && (
                            <button
                              type="button"
                              onClick={() => setSearchTerm('')}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#475569] hover:text-[#475569]"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={openCreateModal}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors flex-shrink-0"
                        style={{
                          fontFamily: 'Raleway, sans-serif',
                          backgroundColor: styles.primaryBg,
                          color: styles.textPrimary,
                          border: `1px solid ${styles.borderColor}`
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = styles.secondaryBg;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = styles.primaryBg;
                        }}
                      >
                        <Plus size={16} />
                        Create Campaign
                      </button>
                    </div>
                  </th>
                </tr>
                <tr style={{ backgroundColor: 'transparent', borderBottom: `2px solid ${styles.borderColor}` }}>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: styles.textPrimary }}>
                    Campaign Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: styles.textPrimary }}>
                    UTM ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: styles.textPrimary }}>
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: styles.textPrimary }}>
                    Count
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: styles.textPrimary }}>
                    Last Event Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider" style={{ color: styles.textPrimary }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {renderTableBody()}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-t" style={{ borderColor: styles.borderColor, color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            <span className="whitespace-nowrap text-sm">
              {filteredCampaigns.length === 0
                ? "0 campaigns"
                : `1-${Math.min(filteredCampaigns.length, tableRowsPerPage)} of ${filteredCampaigns.length}${searchTerm ? ` (filtered from ${campaigns.length})` : ''}`}
            </span>
            <div className="flex items-center gap-2 text-sm ml-auto">
              <span>Rows per page:</span>
              <select
                value={tableRowsPerPage}
                onChange={(e) => setTableRowsPerPage(Number(e.target.value))}
                className="rounded-lg px-2 py-1 text-sm focus:outline-none"
                style={{
                  fontFamily: 'Raleway, sans-serif',
                  backgroundColor: styles.primaryBg,
                  color: styles.textPrimary,
                  border: `1px solid ${styles.borderColor}`
                }}
              >
                {[10, 25, 50, 100].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled
                className="px-2 py-1 rounded-lg cursor-not-allowed"
                style={{
                  backgroundColor: styles.primaryBg,
                  color: styles.textSecondary,
                  border: `1px solid ${styles.borderColor}`,
                  opacity: 0.5
                }}
              >
                &lt;
              </button>
              <button
                type="button"
                disabled
                className="px-2 py-1 rounded-lg cursor-not-allowed"
                style={{
                  backgroundColor: styles.primaryBg,
                  color: styles.textSecondary,
                  border: `1px solid ${styles.borderColor}`,
                  opacity: 0.5
                }}
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateCampaignForm
          onClose={closeCreateModal}
          onCampaignCreated={handleLinkCreated}
        />
      )}

      {showDetailsModal && selectedCampaign && (
        <CampaignDetailsModal
          campaign={selectedCampaign}
          onClose={closeDetailsModal}
        />
      )}


      {/* Delete Confirmation Modal */}
      <Modal
        title={
          <span style={{ 
            color: '#0f172a',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 600
          }}>
            Delete Campaign
          </span>
        }
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmLoading={deleting}
        okText="Delete"
        cancelText="Cancel"
        width={400}
        centered
        styles={{ 
          body: { 
            padding: '20px',
            backgroundColor: '#ffffff'
          },
          header: {
            borderBottom: '1px solid #1d0c46',
            borderRadius: '0px'
          },
          content: {
            borderRadius: '0px',
            border: '1px solid #1d0c46'
          }
        }}
        okButtonProps={{
          style: {
            backgroundColor: '#1d0c46',
            borderColor: '#1d0c46',
            color: '#ffffff',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 500,
            borderRadius: '0px',
            padding: '4px 16px'
          }
        }}
        cancelButtonProps={{
          style: {
            color: '#0f172a',
            borderColor: '#1d0c46',
            backgroundColor: '#ffffff',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 500,
            borderRadius: '0px',
            padding: '4px 16px'
          }
        }}
      >
        <p style={{ 
          fontFamily: 'Raleway, sans-serif',
          fontSize: '16px',
          color: '#0f172a',
          margin: 0
        }}>
          Are you sure you want to delete the campaign "{campaignToDelete?.name}"? 
          This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default Campaigns;


